import { render, h } from 'preact';
import htm from 'htm';

const html = htm.bind(h);

/**
 * facets.js
 * =========
 * Faceted filtering panel for the Algolia-powered SRP.
 *
 * Owns four responsibilities that are deliberately kept separate from
 * search-results.js:
 *   1. URL <-> facet state (read/write)        - "FACET STATE" section
 *   2. Filter-string / disjunctive requests     - "FILTERS" section
 *   3. Rendering the facet panel with Preact    - "RENDER: PANEL" section
 *   4. Rendering the current-filters chip list  - "RENDER: CURRENT FILTERS" section
 *
 * search-results.js is responsible for orchestration: it owns the actual
 * Algolia client, runs the search, and calls into this module to (a) read
 * facet state from the URL, (b) get the extra requests it needs to bundle
 * into its single searchClient.search() call, and (c) hand the results
 * back here to render.
 *
 * ---------------------------------------------------------------------
 * EXPECTED CONFIG SHAPE (per brand, in _config.js)
 * ---------------------------------------------------------------------
 * facets: {
 *   // Order of keys here = display order in the panel.
 *   // Only attributes listed here ever render as a facet section —
 *   // this is the single source of truth for what's visible, so an
 *   // attribute with no entry (e.g. vendor, tags) simply never
 *   // shows up in the UI, labelled or not.
 *   attributes: {
 *     'options.color': { type: 'swatch',   label: 'Color',    disjunctive: true },
 *     'options.size':  { type: 'checkbox', label: 'Size',     disjunctive: true, groupMap: SIZE_GROUP_MAP },
 *     'product_type':  { type: 'checkbox', label: 'Category', disjunctive: true },
 *     'price':         { type: 'range',    label: 'Price',    disjunctive: false },
 *   },
 * 
 *   // groupMap (optional): { rawValue: groupLabel }. When present:
 *   //   - buildClauseForAttribute expands selected group labels back into
 *   //     the raw values they represent before building the filter clause
 *   //   - mergeFacetCounts rolls raw facet counts up into group totals
 *   //   - unmapped raw values pass through as their own ungrouped option
 *   //     (both in the URL/state and in the rendered checkbox list)
 *
 *   // order (optional): string[] or the string 'alpha'.
 *   //   - array: display order for this attribute's values/groups —
 *   //     listed values render first, in the given order; anything
 *   //     present in the facet response but not listed is appended
 *   //     afterward, sorted alphabetically.
 *   //   - 'alpha': every value sorted alphabetically, no priority list.
 *   // Applies to any checkbox/swatch attribute, independent of whether
 *   // groupMap is used. Applied once in mergeFacetCounts (not at render
 *   // time) since that runs once per search response rather than once
 *   // per panel render.
 *
 *   // Empty array hides the sort dropdown entirely — 
 *   // no code changes needed.
 *   sortOptions: [
 *     // { key: 'price_asc', label: 'Price: Low to High', indexName: 'shopify_products_price_asc' },
 *   ],
 * }
 * ---------------------------------------------------------------------
 */

// ==== FACET STATE (URL <-> structured state) =========================

/**
 * Reads the current facet selections out of the URL.
 *
 * Shape returned:
 *   {
 *     'options.color': ['Black', 'White'],  // checkbox/swatch -> string[]
 *     'product_type': ['Women's Tees & Tanks'],
 *     'price': { min: 25, max: 100 }        // range -> {min,max}, either can be null
 *   }
 *
 * Every attribute in facetsConfig.attributes is guaranteed a key in the
 * returned object (empty array / {min:null,max:null} if unset), so
 * render code never has to guard against undefined.
 */
export function getFacetStateFromURL(params, facetsConfig) {
  const state = {};
  for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
    if (def.type === 'range') {
      const min = params.get(`f_${attr}_min`);
      const max = params.get(`f_${attr}_max`);
      state[attr] = {
        min: min !== null && min !== '' ? Number(min) : null,
        max: max !== null && max !== '' ? Number(max) : null,
      };
    } else {
      state[attr] = params.getAll(`f_${attr}`);
    }
  }
  return state;
}

/**
 * Writes facet state into a URLSearchParams instance in place.
 * Called from search-results.js's pushStateToURL alongside q/page.
 */
export function serializeFacetStateToURL(params, facetState, facetsConfig) {
  for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
    if (def.type === 'range') {
      const { min, max } = facetState[attr] || {};
      setOrDelete(params, `f_${attr}_min`, min ?? null);
      setOrDelete(params, `f_${attr}_max`, max ?? null);
    } else {
      params.delete(`f_${attr}`);
      const values = facetState[attr] || [];
      for (const v of values) params.append(`f_${attr}`, v);
    }
  }
}

function setOrDelete(params, key, value) {
  if (value === null || value === undefined || value === '') {
    params.delete(key);
  } else {
    params.set(key, String(value));
  }
}

/** True if any attribute has an active selection — used to know whether to reset pagination. */
export function hasActiveFacets(facetState, facetsConfig) {
  return Object.entries(facetsConfig.attributes).some(([attr, def]) => {
    const value = facetState[attr];
    if (def.type === 'range') return value?.min != null || value?.max != null;
    return Array.isArray(value) && value.length > 0;
  });
}

// ==== FILTERS (Algolia filters string + disjunctive requests) ========

// Escapes characters that would break out of a double-quoted filter value.
function escapeFilterValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Builds the clause for a single attribute, or null if it has no active
 * selection (so it can be safely skipped when joining with AND).
 */
function buildClauseForAttribute(attr, def, value) {
  // Cache the inverted map (groupLabel -> raw values) per groupMap object,
  // since facetsConfig is module-level/stable — no need to recompute per render.
  const invertedGroupMapCache = new WeakMap();
  function getInvertedGroupMap(groupMap) {
    if (invertedGroupMapCache.has(groupMap)) return invertedGroupMapCache.get(groupMap);
    const inverted = Object.entries(groupMap).reduce((acc, [raw, group]) => {
      (acc[group] ??= []).push(raw);
      return acc;
    }, {});
    invertedGroupMapCache.set(groupMap, inverted);
    return inverted;
  }

  // Expands selected group labels into their raw values. A selected value
  // that ISN'T a group label (an unmapped raw value shown ungrouped) passes
  // through unchanged.
  function expandGroupedValues(values, groupMap) {
    if (!groupMap) return values;
    const inverted = getInvertedGroupMap(groupMap);
    return values.flatMap(v => inverted[v] ?? [v]);
  }

  if (def.type === 'range') {
    if (value == null) return null;
    const parts = [];
    if (value.min != null) parts.push(`${attr} >= ${value.min}`);
    if (value.max != null) parts.push(`${attr} <= ${value.max}`);
    if (!parts.length) return null;
    return parts.length > 1 ? `(${parts.join(' AND ')})` : parts[0];
  }
  // checkbox / swatch: OR within the attribute (multi-select),
  // AND against every other attribute (handled by the caller).
  if (!Array.isArray(value) || value.length === 0) return null;
  const rawValues = expandGroupedValues(value, def.groupMap);
  const ored = rawValues.map(v => `${attr}:"${escapeFilterValue(v)}"`).join(' OR ');
  return rawValues.length > 1 ? `(${ored})` : ored;
}

/**
 * Builds the full Algolia filters string from facet state.
 *
 * @param excludeAttr - when set, that attribute's own clause is omitted.
 * This is the crux of disjunctive faceting: to get accurate counts for
 * "how many results if I *also* picked this color", the count request
 * must apply every filter except the color filter itself.
 */
export function buildFiltersString(facetState, facetsConfig, excludeAttr = null) {
  const clauses = [];
  for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
    if (attr === excludeAttr) continue;
    const clause = buildClauseForAttribute(attr, def, facetState[attr]);
    if (clause) clauses.push(clause);
  }
  return clauses.join(' AND ');
}

/**
 * Builds the extra Algolia requests needed for disjunctive facet counts.
 *
 * Only attributes that are (a) marked disjunctive: true in config AND
 * (b) currently have an active selection need an extra request — an
 * attribute with no active refinement already gets correct counts from
 * the main search request, since nothing is excluding any of its own
 * values. This keeps the request count proportional to how many filters
 * are actually active, not a fixed cost per page load.
 *
 * Returns { requests, attributes } where attributes[i] is the facet
 * attribute that requests[i] was built for — use this to map results
 * back after the batched searchClient.search() call resolves.
 */
export function getDisjunctiveRequests({ query, facetState, facetsConfig, indexName }) {
  const requests = [];
  const attributes = [];
  for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
    if (!def.disjunctive) continue;
    const value = facetState[attr];
    const hasSelection = Array.isArray(value) && value.length > 0;
    if (!hasSelection) continue;

    requests.push({
      indexName,
      query,
      filters: buildFiltersString(facetState, facetsConfig, attr) || undefined,
      facets: [attr],
      hitsPerPage: 0,
      page: 0,
    });
    attributes.push(attr);
  }
  return { requests, attributes };
}

function rollUpCounts(rawCounts, groupMap) {
  if (!groupMap) return rawCounts;
  const rolled = {};
  for (const [rawValue, count] of Object.entries(rawCounts)) {
    const label = groupMap[rawValue] ?? rawValue; // unmapped passthrough
    rolled[label] = (rolled[label] || 0) + count;
  }
  return rolled;
}

/**
 * Reorders a { [value]: count } map per a display-order config.
 *
 * `order` can be either:
 *   - the string 'alpha': every value is sorted alphabetically, full stop.
 *   - an array of values: listed values come first, in that order;
 *     anything present in `counts` but not listed is appended afterward,
 *     sorted alphabetically.
 *
 * Rendering (CheckboxList's Object.keys(counts)) relies on JS preserving
 * string-key insertion order, so building the object in the right order
 * here is sufficient — no render-side sorting needed.
 */
function applyDisplayOrder(counts, order) {
  if (!order || (Array.isArray(order) && !order.length)) return counts;

  if (order === 'alpha') {
    const ordered = {};
    for (const value of Object.keys(counts).sort((a, b) => a.localeCompare(b))) {
      ordered[value] = counts[value];
    }
    return ordered;
  }

  const remaining = new Set(Object.keys(counts));
  const ordered = {};

  for (const value of order) {
    if (remaining.has(value)) {
      ordered[value] = counts[value];
      remaining.delete(value);
    }
  }

  for (const value of [...remaining].sort((a, b) => a.localeCompare(b))) {
    ordered[value] = counts[value];
  }

  return ordered;
}

/**
 * Merges main-request facet counts with any disjunctive-corrected counts.
 * Returns a plain { [attribute]: { [value]: count } } map ready to render,
 * plus min/max stats for range attributes.
 *
 * @param mainFacets - productResults.facets from the primary request
 * @param mainFacetsStats - productResults.facets_stats from the primary request
 * @param disjunctiveResults - array of results, parallel to attributes
 * @param attributes - array of attribute names, parallel to disjunctiveResults
 */
export function mergeFacetCounts({ mainFacets, mainFacetsStats, disjunctiveResults, attributes, facetsConfig }) {
  const counts = { ...mainFacets };
  disjunctiveResults.forEach((result, i) => {
    const attr = attributes[i];
    counts[attr] = result.facets?.[attr] || {};
  });

  for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
    if (def.groupMap && counts[attr]) {
      counts[attr] = rollUpCounts(counts[attr], def.groupMap);
    }
    if (def.order && counts[attr]) {
      counts[attr] = applyDisplayOrder(counts[attr], def.order);
    }
  }

  return { counts, stats: mainFacetsStats || {} };
}

// ==== SORT (index-swap, not a filter) =================================

/** Resolves which index to query based on the URL's sort param. Falls back
 * to the brand's default product index when unset or unrecognized. */
export function getIndexNameForSort(sortKey, facetsConfig, defaultIndexName) {
  const match = (facetsConfig.sortOptions || []).find(o => o.key === sortKey);
  return match ? match.indexName : defaultIndexName;
}

// ==== SHARED: current-filter-chip building =============================
// Attribute-type-agnostic — walks every attribute in facetsConfig so a
// removal chip shows up for swatch, checkbox, AND range selections. 
// A plain data-builder with no knowledge of where it's rendered.
function buildCurrentFilterChips(facetState, facetsConfig, onFacetChange) {
  const chips = [];

  for (const [attr, def] of Object.entries(facetsConfig.attributes)) {
    const value = facetState[attr];

    if (def.type === 'range') {
      if (value == null || (value.min == null && value.max == null)) continue;
      const text = [
        value.min != null ? `$${value.min}` : '',
        value.max != null ? `$${value.max}` : '',
      ].filter(Boolean).join(' - ');

      chips.push({
        key: attr,
        label: def.label,
        text,
        onRemove: () => onFacetChange(attr, { min: null, max: null }),
      });
      continue;
    }

    if (!Array.isArray(value) || value.length === 0) continue;
    value.forEach(item => {
      if (!item) return;
      chips.push({
        key: `${attr}:${item}`,
        label: def.label,
        text: item,
        onRemove: () => onFacetChange(attr, value.filter(v => v !== item)),
      });
    });
  }

  return chips;
}

function CurrentFilterChips({ chips, onClear }) {
  if (!chips.length) return null;
  return html`
    <button
      type="button"
      class="srp-facets__clear"
      onClick=${onClear}
    >
      Clear All
    </button>

    <div class="srp-facets__current-list">
      ${chips.map(chip => html`
        <button
          key=${chip.key}
          type="button"
          class="srp-facets__current srp-facets__current--${chip.label}"
          aria-pressed="true"
          aria-label=${`${chip.label} ${chip.text}`}
          title=${chip.text}
          onClick=${chip.onRemove}
        >
          ${chip.text}
        </button>
      `)}
    </div>
  `;
}

// ==== RENDER: CURRENT FILTERS ==========================================

/**
 * Mounts the current-filters chip list into its own container, independent
 * of the facet panel. 
 *
 * Returns an update(data) function — call it every time facet state
 * changes, same pattern as mountFacetPanel.
 *
 * @param container - DOM node to render into, or null/undefined
 * @param facetsConfig - this brand's algoliaConfig[brand].facets
 * @param handlers - { onFacetChange(attr, nextValue) } — same handlers object
 *   passed to mountFacetPanel, so removing a chip drives the exact same
 *   state-update path as toggling it off in the panel.
 */
export function mountCurrentFilters(container, facetsConfig, handlers) {
  if (!container) {
    // No-op update fn: keeps call sites simple (no need to guard every
    // update() call) on pages where the snippet isn't rendered.
    return () => { };
  }

  function update({ facetState }) {
    const chips = buildCurrentFilterChips(facetState, facetsConfig, handlers.onFacetChange);
    render(html`<${CurrentFilterChips} chips=${chips} onClear=${handlers.onClear} />`, container);
  }

  return update;
}

// ==== RENDER: PANEL =====================================================

/**
 * Mounts the facet panel and returns an update(data) function.
 * Call update every time new search results come back; the panel
 * re-renders in place (Preact diffs, so this is cheap).
 *
 * @param container - DOM node to render into
 * @param facetsConfig - this brand's algoliaConfig[brand].facets
 * @param handlers - { onFacetChange(attr, nextValue), onSortChange(key), onClear() }
 */
export function mountFacetPanel(container, facetsConfig, handlers) {
  let isPanelOpen = false;

  function toggleFacetPanel() {
    isPanelOpen = !isPanelOpen;

    document.body.classList.toggle(
      "algolia-facet-panel-open",
      isPanelOpen
    );

    if (!isPanelOpen) {
      const facetSectionToggles = document.querySelectorAll('.srp-facets__section__toggle');

      facetSectionToggles.forEach((facetSectionButton) => {
        facetSectionButton.setAttribute("aria-expanded", "false");
        facetSectionButton.parentElement.classList.remove("srp-facets__section__label--expanded");
      });
    }

    // Re-render with the new state.
    update(currentData);
  }

  let currentData = {};

  function update(data) {
    currentData = data;

    const {
      facetState,
      counts,
      stats,
      sortKey,
      colorSwatchMap,
    } = data;

    render(
      html`
        <button 
          id="algoliaFacetsToggle" 
          class="button srp-facets__toggle" 
          aria-expanded=${isPanelOpen}
          onClick=${toggleFacetPanel}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="square"><path d="M10 5H3"/><path d="M12 19H3"/><path d="M14 3v4"/><path d="M16 17v4"/><path d="M21 12h-9"/><path d="M21 19h-5"/><path d="M21 5h-7"/><path d="M8 10v4"/><path d="M8 12H3"/></svg>
          Sort and Filter
        </button>

        <div class="srp-facets__inner">
          <button 
            id="algoliaFacetsClose" 
            class="srp-facets__close" 
            onClick=${toggleFacetPanel}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M5.293 6.707l5.293 5.293-5.293 5.293c-0.391 0.391-0.391 1.024 0 1.414s1.024 0.391 1.414 0l5.293-5.293 5.293 5.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-5.293-5.293 5.293-5.293c0.391-0.391 0.391-1.024 0-1.414s-1.024-0.391-1.414 0l-5.293 5.293-5.293-5.293c-0.391-0.391-1.024-0.391-1.414 0s-0.391 1.024 0 1.414z"></path></svg>
            <span class="sr-only">Close Sort and Filter</span>
          </button>

          <div class="srp-facets__all-facets">
            ${facetsConfig.sortOptions?.length > 0 && html`
              <${SortDropdown}
                options=${facetsConfig.sortOptions}
                value=${sortKey}
                onChange=${handlers.onSortChange}
              />
            `}
            ${Object.entries(facetsConfig.attributes).map(([attr, def]) => {
              const label = def.label; // attributes without a label never reach here — see facetsConfig contract above
              const value = facetState[attr];
              // Range has no `counts` entry (it uses `stats` instead), so never hide it here.
              // Checkbox/swatch sections hide whenever their counts object has no keys.
              const hidden = def.type !== 'range' && Object.keys(counts[attr] || {}).length === 0;

              if (def.type === 'swatch') {
                return html`
                      <${FacetSection} hidden=${hidden} label=${label}>
                        <${SwatchList}
                          attr=${attr}
                          counts=${counts[attr] || {}}
                          selected=${value}
                          colorSwatchMap=${colorSwatchMap}
                          onChange=${(next) => handlers.onFacetChange(attr, next)}
                        />
                      <//>
                    `;
              }
              if (def.type === 'checkbox') {
                return html`
                      <${FacetSection} hidden=${hidden} label=${label}>
                        <${CheckboxList}
                          attr=${attr}
                          counts=${counts[attr] || {}}
                          selected=${value}
                          onChange=${(next) => handlers.onFacetChange(attr, next)}
                        />
                      <//>
                    `;
              }
              if (def.type === 'range') {
                return html`
                      <${FacetSection} hidden=${hidden} label=${label}>
                        <${RangeInputs}
                          attr=${attr}
                          stats=${stats[attr] || {}}
                          value=${value}
                          onChange=${(next) => handlers.onFacetChange(attr, next)}
                        />
                      <//>
                    `;
              }
              return null;
            })}
          </div>
        </div>
        <span class="srp-facets__shade"></span>
      `,
      container
    );
  }

  return update;
}

// Wraps a facet type's UI with its label. Sections are always expanded
// (no accordion) per current design — this is the seam to add a
// collapse toggle later without touching each facet type's markup.
function FacetSection({ label, hidden, children }) {
  return html`
    <div class="srp-facets__section" hidden="${hidden}" data-facet-section=${label}>
      <h3 class="h5 srp-facets__section__label">    
        <button aria-expanded="false" class="srp-facets__section__toggle">
          ${label}
          <svg class="srp-facets__toggle__icon" width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M4 7L3 7L3 -4.37114e-08L4 0L4 7Z" fill="black" stroke="black" stroke-width="0.5"></path>
            <path d="M7 3V4H0V3H7Z" fill="black" stroke="black" stroke-width="0.5"></path>
            <defs></defs>
          </svg>
        </button>
      </h3>
      ${children}
    </div>
  `;
}

function SwatchList({ attr, counts, selected, colorSwatchMap, onChange }) {
  const values = Object.keys(counts);
  if (!values.length) return null;

  function toggle(value) {
    const isSelected = selected.includes(value);
    const next = isSelected
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(next);
  }

  return html`
    <ul class="srp-facets__swatch-list">
      ${values.map(value => {
    const isSelected = selected.includes(value);
    const swatch = colorSwatchMap[value];
    const style = swatch?.image
      ? { '--swatch-image': `url(${swatch.image})` }
      : { '--swatch-color': swatch?.color };

    if (swatch?.color || swatch?.image) {
      return html`
            <li>
              <button
                type="button"
                class="srp-facets__swatch ${isSelected ? 'is-selected' : ''}"
                style=${style}
                aria-pressed=${isSelected}
                aria-label="${value} (${counts[value]})"
                data-tooltip=${value}
                onClick=${() => toggle(value)}
              ></button>
            </li>
          `;
    } else {
      return null;
    }
  })}
    </ul>
  `;
}

function CheckboxList({ attr, counts, selected, onChange }) {
  const values = Object.keys(counts);
  if (!values.length) return null;

  function toggle(value) {
    const isSelected = selected.includes(value);
    const next = isSelected
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(next);
  }

  return html`
    <ul class="srp-facets__checkbox-list">
      ${values.map(value => {
    const inputId = `facet-${attr}-${value}`.replace(/[^a-zA-Z0-9_-]/g, '-');
    return html`
          <li class="srp-facets__checkbox-item">
            <input
              type="checkbox"
              id=${inputId}
              checked=${selected.includes(value)}
              onChange=${() => toggle(value)}
            />
            <label for=${inputId}>${value} <span class="srp-facets__count">(${counts[value]})</span></label>
          </li>
        `;
  })}
    </ul>
  `;
}

/**
 * Min/max price inputs (replaces the old dual-handle slider).
 *
 * Display defaults:
 *   - min input shows 0 when no filter is active
 *   - max input shows the current search combination's max price
 *     (Algolia's facets_stats.max for this attribute) when no filter
 *     is active
 *
 * Behavior: typing does NOT filter live. The value only commits to
 * facetState (and triggers a re-search) on blur or Enter — matches
 * the debounced-typing choice for this facet. Committing a value that
 * equals the current default (0 for min, stats.max for max) is treated
 * as "no filter" so hasActiveFacets/chips stay consistent with the
 * null-based state model used elsewhere in this file. Min/max are
 * clamped against each other so min can never be typed above max
 * (and vice versa).
 */
function RangeInputs({ attr, stats, value, onChange }) {
  const boundsMax = stats.max ?? 0;
  const defaultMin = 0;
  const defaultMax = boundsMax;

  const currentMin = value?.min ?? defaultMin;
  const currentMax = value?.max ?? defaultMax;

  function parse(raw) {
    if (raw === '') return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }

  function commitMin(e) {
    let nextMin = parse(e.target.value);
    if (nextMin != null) {
      nextMin = Math.max(0, nextMin);
      const activeMax = value?.max ?? defaultMax;
      if (nextMin > activeMax) nextMin = activeMax;
    }
    if (nextMin === defaultMin) nextMin = null; // back to default = not an active filter
    e.target.value = nextMin ?? defaultMin;
    onChange({ min: nextMin, max: value?.max ?? null });
  }

  function commitMax(e) {
    let nextMax = parse(e.target.value);
    if (nextMax != null) {
      const activeMin = value?.min ?? defaultMin;
      if (nextMax < activeMin) nextMax = activeMin;
      if (nextMax > boundsMax) nextMax = boundsMax;
    }
    if (nextMax === defaultMax) nextMax = null; // back to default = not an active filter
    e.target.value = nextMax ?? defaultMax;
    onChange({ min: value?.min ?? null, max: nextMax });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') e.target.blur(); // blur triggers the commit above
  }

  return html`
    <div class="srp-range-facet">
      <label class="srp-range-facet__field">
        <span class="srp-range-facet__field__label">Min</span>
        <span class="srp-range-facet__field__dollar-sign">$</span>
        <input
          type="number"
          inputmode="decimal"
          min="0"
          max=${boundsMax}
          step="1"
          value=${currentMin}
          onKeyDown=${handleKeyDown}
          onBlur=${commitMin}
        />
      </label>
      <span class="srp-range-facet__sep" aria-hidden="true"></span>
      <label class="srp-range-facet__field">
        <span class="srp-range-facet__field__label">Max</span>
        <span class="srp-range-facet__field__dollar-sign">$</span>
        <input
          type="number"
          inputmode="decimal"
          min="0"
          max=${boundsMax}
          step="1"
          value=${currentMax}
          onKeyDown=${handleKeyDown}
          onBlur=${commitMax}
        />
      </label>
    </div>
  `;
}

function SortDropdown({ options, value, onChange }) {
  return html`
    <div class="srp-facets__sort">
      <label for="srpSortSelect" class="h5">Sort</label>
      <select id="srpSortSelect" value=${value || ''} onChange=${(e) => onChange(e.target.value)}>
        <option value="">Featured</option>
        ${options.map(o => html`<option value=${o.key}>${o.label}</option>`)}
      </select>
    </div>
  `;
}
